import React from 'react';
import { 
  ExternalLink, Compass, Calendar, Sparkles, Zap, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const OtherResourcesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-8" data-testid="other-resources-page">
      {/* Hero Section */}
      <div className="text-center pb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mb-4">
          <Zap className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-3xl font-bold text-stone-900 mb-3">Ready to Accelerate?</h1>
        <p className="text-lg text-stone-600 max-w-2xl mx-auto">
          If you're feeling momentum building and don't want to lose it, here are ways to go deeper and move faster.
        </p>
      </div>

      {/* Clarity First - Alignment Audit */}
      <div className="card-cosmic p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
            <Compass className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-primary uppercase tracking-wide mb-1">Clarity First</p>
            <h2 className="text-2xl font-bold text-stone-900">Alignment Audit</h2>
          </div>
        </div>
        
        <div className="ml-0 md:ml-[4.5rem] space-y-4">
          <p className="text-stone-600 leading-relaxed">
            If something in your life feels stuck or unclear, start here.
          </p>
          <p className="text-stone-600 leading-relaxed">
            In this complimentary Alignment Activation, we'll uncover the pattern shaping your current reality and clarify the next step that moves you forward.
          </p>
          <Button
            onClick={() => window.open('http://heatherstewart.coach/alignment-audit', '_blank')}
            className="btn-cosmic mt-4"
            data-testid="alignment-audit-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Take the Alignment Audit
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* 30-Day Alignment Lab */}
      <div className="card-cosmic p-8 border-2 border-primary/20 hover:border-primary/40 transition-all">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-primary uppercase tracking-wide mb-1">Transform in 30 Days</p>
            <h2 className="text-2xl font-bold text-stone-900">30-Day Alignment Lab</h2>
          </div>
        </div>
        
        <div className="ml-0 md:ml-[4.5rem] space-y-4">
          <p className="text-stone-600 leading-relaxed">
            A transformative 30-day journey practicing choosing yourself without guilt. 4 weeks of structured content, live Wednesday check-ins, and a supportive community.
          </p>
          <ul className="space-y-2 text-stone-600">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Weekly themes: Rest is Worth, Time Boundaries, Availability Is Not Love, The Clean No
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Monday experiments + Wednesday live calls + Friday community threads
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Lifetime access upon completion
            </li>
          </ul>
          <Button
            onClick={() => navigate('/dashboard/alignment-lab')}
            className="btn-cosmic mt-4"
            data-testid="alignment-lab-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Learn More About the Alignment Lab
          </Button>
        </div>
      </div>

      {/* Private Coaching Support, Monthly */}
      <div className="card-cosmic p-8 border-2 border-fuchsia-500/20 hover:border-fuchsia-500/40 transition-all">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-rose-500 flex items-center justify-center flex-shrink-0">
            <Heart className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-fuchsia-600 uppercase tracking-wide mb-1">Private Coaching Support, Monthly</p>
            <h2 className="text-2xl font-bold text-stone-900">Monthly Coaching Support</h2>
          </div>
        </div>
        
        <div className="ml-0 md:ml-[4.5rem] space-y-4">
          <p className="text-stone-600 leading-relaxed">
            You'll receive 60 minutes of private coaching each month to use in the way that serves you best. Choose one 60-minute session for a deeper dive, or two 30-minute sessions for ongoing support, accountability, and real-life integration.
          </p>
          <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-5">
            <h3 className="font-bold text-stone-900 mb-2">Your Monthly Alignment Support</h3>
            <p className="text-stone-600 leading-relaxed">
              Each month, you'll have 60 minutes of private coaching to help you reconnect, reset, and move forward. You can choose one 60-minute session for a deeper conversation or two 30-minute sessions for ongoing support and accountability.
            </p>
          </div>
          <p className="text-stone-500 text-sm">Ongoing monthly subscription — cancel anytime.</p>
          <Button
            onClick={() => window.open('https://prosperityflowcoaching.thrivecart.com/60-min-monthly/', '_blank')}
            className="bg-gradient-to-r from-fuchsia-500 to-rose-500 hover:from-fuchsia-600 hover:to-rose-600 text-white mt-4"
            data-testid="monthly-coaching-btn"
          >
            <Heart className="w-4 h-4 mr-2" />
            Sign Up for Monthly Coaching
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Anchor the Change - 3-Month Coaching */}
      <div className="card-cosmic p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-accent uppercase tracking-wide mb-1">Anchor the Change</p>
            <h2 className="text-2xl font-bold text-stone-900">3-Month Self-Ownership Stabilization (1:1)</h2>
          </div>
        </div>
        
        <div className="ml-0 md:ml-[4.5rem] space-y-4">
          <p className="text-stone-600 leading-relaxed">
            If you're ready to stop having powerful conversations and start living differently, this is where acceleration becomes transformation.
          </p>
          <p className="text-stone-600 leading-relaxed">
            Over three months, we dismantle the patterns shaping your life and stabilize self-ownership across your work, relationships, body, money, and identity.
          </p>
          <div className="py-2">
            <p className="text-stone-700 font-medium italic">This is depth.</p>
            <p className="text-stone-700 font-medium italic">This is integration.</p>
            <p className="text-stone-700 font-medium italic">This is lasting change.</p>
          </div>
          <Button
            onClick={() => window.open('http://heatherstewart.coach/alignment-audit', '_blank')}
            className="btn-cosmic mt-4"
            data-testid="apply-activation-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Apply for an Alignment Activation to begin
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OtherResourcesPage;
